import { midiToName, parseMidiOrNoteName } from '../util/note';

export type RecognitionReplayNoteSource = 'capture' | 'selected' | 'stable' | 'onset';

export type RecognitionReplayFrame = {
  capturedAtMs?: number;
  midiNotes?: number[];
  onsetMidiNotes?: number[];
  selectedMidiNotes?: number[];
  captureMidiNotes?: number[];
  residualEnergy?: number | null;
};

export type RecognitionReplayFrameScore = {
  frameIndex: number;
  capturedAtMs?: number;
  actualMidiNotes: number[];
  truePositiveMidiNotes: number[];
  falsePositiveMidiNotes: number[];
  falseNegativeMidiNotes: number[];
  precision: number;
  recall: number;
  f1: number;
  exact: boolean;
  residualEnergy: number | null;
};

export type RecognitionReplayEvaluation = {
  expectedMidiNotes: number[];
  expectedNoteNames: string[];
  noteSource: RecognitionReplayNoteSource;
  frameCount: number;
  scoredFrameCount: number;
  exactFrameCount: number;
  exactFrameRate: number;
  observedFrameCount: number;
  exactObservedFrameCount: number;
  exactObservedFrameRate: number;
  observedPrecision: number;
  observedRecall: number;
  observedF1: number;
  bestFrame: RecognitionReplayFrameScore | null;
  precision: number;
  recall: number;
  f1: number;
  falsePositiveMidiNotes: number[];
  falseNegativeMidiNotes: number[];
};

export type ParsedRecognitionNoteInput = {
  midiNotes: number[];
  invalidTokens: string[];
};

const normalizeMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((a, b) => a - b);

const getFrameNotes = (frame: RecognitionReplayFrame, noteSource: RecognitionReplayNoteSource) => {
  if (noteSource === 'capture') return frame.captureMidiNotes ?? [];
  if (noteSource === 'selected') return frame.selectedMidiNotes ?? [];
  if (noteSource === 'onset') return frame.onsetMidiNotes ?? [];
  return frame.midiNotes ?? [];
};

const scoreFrame = (
  expectedMidiNotes: number[],
  actualMidiNotes: number[],
  frameIndex: number,
  frame: RecognitionReplayFrame,
): RecognitionReplayFrameScore => {
  const expected = new Set(expectedMidiNotes);
  const actual = new Set(normalizeMidiNotes(actualMidiNotes));
  const truePositiveMidiNotes = Array.from(actual).filter((midi) => expected.has(midi));
  const falsePositiveMidiNotes = Array.from(actual).filter((midi) => !expected.has(midi));
  const falseNegativeMidiNotes = Array.from(expected).filter((midi) => !actual.has(midi));
  const precision =
    actual.size === 0 ? (expected.size === 0 ? 1 : 0) : truePositiveMidiNotes.length / actual.size;
  const recall =
    expected.size === 0 ? 1 : truePositiveMidiNotes.length / Math.max(expected.size, 1);
  const f1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
  const exact = falsePositiveMidiNotes.length === 0 && falseNegativeMidiNotes.length === 0;

  return {
    frameIndex,
    capturedAtMs: frame.capturedAtMs,
    actualMidiNotes: Array.from(actual).sort((a, b) => a - b),
    truePositiveMidiNotes: truePositiveMidiNotes.sort((a, b) => a - b),
    falsePositiveMidiNotes: falsePositiveMidiNotes.sort((a, b) => a - b),
    falseNegativeMidiNotes: falseNegativeMidiNotes.sort((a, b) => a - b),
    precision,
    recall,
    f1,
    exact,
    residualEnergy: frame.residualEnergy ?? null,
  };
};

const compareFrameScore = (a: RecognitionReplayFrameScore, b: RecognitionReplayFrameScore) => {
  if (a.exact !== b.exact) return a.exact ? 1 : -1;
  if (a.f1 !== b.f1) return a.f1 - b.f1;
  if (a.recall !== b.recall) return a.recall - b.recall;
  if (a.precision !== b.precision) return a.precision - b.precision;

  const aResidual = a.residualEnergy ?? Number.POSITIVE_INFINITY;
  const bResidual = b.residualEnergy ?? Number.POSITIVE_INFINITY;
  if (aResidual !== bResidual) return bResidual - aResidual;

  return b.actualMidiNotes.length - a.actualMidiNotes.length;
};

export const parseRecognitionExpectedNotes = (input: string): ParsedRecognitionNoteInput => {
  const tokens = input
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  const midiNotes: number[] = [];
  const invalidTokens: string[] = [];

  tokens.forEach((token) => {
    const midi = parseMidiOrNoteName(token);
    if (midi == null) {
      invalidTokens.push(token);
      return;
    }
    midiNotes.push(midi);
  });

  return {
    midiNotes: normalizeMidiNotes(midiNotes),
    invalidTokens,
  };
};

export const evaluateRecognitionReplayFrames = (
  frames: RecognitionReplayFrame[],
  expectedMidiNotes: number[],
  noteSource: RecognitionReplayNoteSource = 'capture',
): RecognitionReplayEvaluation => {
  const expected = normalizeMidiNotes(expectedMidiNotes);
  const frameScores = frames.map((frame, frameIndex) =>
    scoreFrame(expected, getFrameNotes(frame, noteSource), frameIndex, frame),
  );
  const scoredFrameScores = frameScores.filter(
    (score) => score.actualMidiNotes.length > 0 || expected.length > 0,
  );
  const bestFrame = scoredFrameScores.reduce<RecognitionReplayFrameScore | null>(
    (best, score) => (!best || compareFrameScore(score, best) > 0 ? score : best),
    null,
  );
  const exactFrameCount = frameScores.filter((score) => score.exact).length;
  const observedFrameScores = frameScores.filter((score) => score.actualMidiNotes.length > 0);
  const exactObservedFrameCount = observedFrameScores.filter((score) => score.exact).length;
  const observedTruePositiveCount = observedFrameScores.reduce(
    (sum, score) => sum + score.truePositiveMidiNotes.length,
    0,
  );
  const observedFalsePositiveCount = observedFrameScores.reduce(
    (sum, score) => sum + score.falsePositiveMidiNotes.length,
    0,
  );
  const observedFalseNegativeCount = observedFrameScores.reduce(
    (sum, score) => sum + score.falseNegativeMidiNotes.length,
    0,
  );
  const observedPrecisionDenominator = observedTruePositiveCount + observedFalsePositiveCount;
  const observedRecallDenominator = observedTruePositiveCount + observedFalseNegativeCount;
  const observedPrecision =
    observedPrecisionDenominator > 0
      ? observedTruePositiveCount / observedPrecisionDenominator
      : expected.length === 0
        ? 1
        : 0;
  const observedRecall =
    observedRecallDenominator > 0
      ? observedTruePositiveCount / observedRecallDenominator
      : expected.length === 0
        ? 1
        : 0;
  const observedF1 =
    observedPrecision + observedRecall > 0
      ? (2 * observedPrecision * observedRecall) / (observedPrecision + observedRecall)
      : 0;

  return {
    expectedMidiNotes: expected,
    expectedNoteNames: expected.map(midiToName),
    noteSource,
    frameCount: frames.length,
    scoredFrameCount: scoredFrameScores.length,
    exactFrameCount,
    exactFrameRate: frames.length > 0 ? exactFrameCount / frames.length : 0,
    observedFrameCount: observedFrameScores.length,
    exactObservedFrameCount,
    exactObservedFrameRate:
      observedFrameScores.length > 0
        ? exactObservedFrameCount / observedFrameScores.length
        : expected.length === 0
          ? 1
          : 0,
    observedPrecision,
    observedRecall,
    observedF1,
    bestFrame,
    precision: bestFrame?.precision ?? (expected.length === 0 ? 1 : 0),
    recall: bestFrame?.recall ?? (expected.length === 0 ? 1 : 0),
    f1: bestFrame?.f1 ?? (expected.length === 0 ? 1 : 0),
    falsePositiveMidiNotes: bestFrame?.falsePositiveMidiNotes ?? [],
    falseNegativeMidiNotes: bestFrame?.falseNegativeMidiNotes ?? expected,
  };
};
